import React, { useState } from "react";
import { motion } from "framer-motion";
import api from "../../utils/api";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

export default function ContactForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    smsConsent: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t("contact_section.form.validation.name_required");
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("contact_section.form.validation.name_min");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("contact_section.form.validation.email_required");
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      newErrors.email = t("contact_section.form.validation.email_invalid");
    }

    if (!formData.phone.trim()) {
      newErrors.phone = t("contact_section.form.validation.phone_required");
    }

    if (!formData.subject) {
      newErrors.subject = t("contact_section.form.validation.subject_required");
    }

    if (!formData.message.trim()) {
      newErrors.message = t("contact_section.form.validation.message_required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = {
        full_name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      };

      await api.post("/contact", payload);

      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        smsConsent: false,
      });
      setErrors({});

      Swal.fire({
        icon: "success",
        title: t("contact_section.form.success.title"),
        text: t("contact_section.form.success.text"),
        timer: 3000,
        showConfirmButton: false
      });

    } catch (error) {
      console.error("Contact Form Error:", error);
      Swal.fire({
        icon: "error",
        title: t("contact_section.form.error.title"),
        text: error.response?.data?.message || t("contact_section.form.error.text"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-16 md:py-24 bg-white">
      <div className="absolute inset-x-0 -top-24 -z-10 h-48 bg-gradient-to-b from-blue-50/70 to-transparent" />
      <div className="container">
        <motion.div
          className="relative overflow-hidden rounded-3xl bg-white/80 backdrop-blur-xl shadow-[0_20px_80px_rgba(2,6,23,0.08)] ring-1 ring-black/5"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="relative px-6 py-6 md:px-8 bg-[radial-gradient(1200px_200px_at_0%_0%,rgba(59,130,246,0.06),transparent)]">
            <div className="absolute right-0 top-0 -translate-y-1/3 translate-x-1/3 w-52 h-52 rounded-full bg-blue-100/40 blur-3xl" />
            <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-gray-900">
              {t("contact_section.form.title")}
            </h2>
            <p className="mt-1 text-gray-600">
              {t("contact_section.form.urgent_help")}{" "}
              <a
                className="font-semibold text-blue-700 hover:text-blue-800"
                href="tel:3108802133"
              >
                +1 3108802133
              </a>
              .
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-8 md:px-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="group">
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  {t("contact_section.form.name_label")}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("contact_section.form.name_placeholder")}
                    className={`peer w-full rounded-xl border px-4 py-3 pr-11 text-gray-900 placeholder-gray-400 outline-none focus:ring-4 transition shadow-sm ${errors.name
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
                      : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15"
                      }`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 20a7 7 0 1114 0"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="12"
                        cy="7"
                        r="4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                    </svg>
                  </span>
                </div>
                {errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.name}</p>
                )}
              </div>

              <div className="group">
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  {t("contact_section.form.email_label")}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("contact_section.form.email_placeholder")}
                    className={`peer w-full rounded-xl border px-4 py-3 pr-11 text-gray-900 placeholder-gray-400 outline-none focus:ring-4 transition shadow-sm ${errors.email
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
                      : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15"
                      }`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4 6l8 5 8-5"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                      <rect
                        x="4"
                        y="6"
                        width="16"
                        height="12"
                        rx="2"
                        stroke="currentColor"
                        strokeWidth="1.6"
                      />
                    </svg>
                  </span>
                </div>
                {errors.email && (
                  <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="group">
                <label
                  htmlFor="phone"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  {t("contact_section.form.phone_label")}
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t("contact_section.form.phone_placeholder")}
                    className={`peer w-full rounded-xl border px-4 py-3 pr-11 text-gray-900 placeholder-gray-400 outline-none focus:ring-4 transition shadow-sm ${errors.phone
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
                      : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15"
                      }`}
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M3 5a2 2 0 012-2h3l2 5-3 1a11 11 0 005 5l1-3 5 2v3a2 2 0 01-2 2h-1C9 21 3 15 3 7V5z"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </div>
                {errors.phone && (
                  <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.phone}</p>
                )}
              </div>

              <div className="group">
                <label
                  htmlFor="subject"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  {t("contact_section.form.subjects.placeholder")} *
                </label>
                <div className="relative">
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className={`w-full appearance-none rounded-xl border px-4 py-3 pr-11 text-gray-900 outline-none focus:ring-4 transition shadow-sm ${errors.subject
                      ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
                      : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15"
                      }`}
                  >
                    <option value="">{t("contact_section.form.subjects.placeholder")}</option>
                    <option value="immigration-inquiry">
                      {t("contact_section.form.subjects.immigration_inquiry")}
                    </option>
                    <option value="lawyer-matching">
                      {t("contact_section.form.subjects.lawyer_matching")}
                    </option>
                    <option value="general-question">
                      {t("contact_section.form.subjects.general_question")}
                    </option>
                    <option value="payment-inquiry">
                      {t("contact_section.form.subjects.payment_inquiry")}
                    </option>
                    <option value="other">{t("contact_section.form.subjects.other")}</option>
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </div>
                {errors.subject && (
                  <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.subject}</p>
                )}
              </div>
            </div>

            <div className="group">
              <label
                htmlFor="message"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("contact_section.form.message_label")}
              </label>
              <div className="relative">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder={t("contact_section.form.message_placeholder")}
                  className={`w-full rounded-xl border px-4 py-3 text-gray-900 placeholder-gray-400 outline-none focus:ring-4 transition shadow-sm resize-none ${errors.message
                    ? "border-red-400 focus:border-red-500 focus:ring-red-500/15"
                    : "border-gray-200 focus:border-blue-500 focus:ring-blue-500/15"
                    }`}
                />
                <span className="pointer-events-none absolute right-3 top-3 text-gray-300">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 4h16v12H5l-1 4V4z"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
              {errors.message && (
                <p className="mt-1.5 text-sm text-red-600 font-medium">{errors.message}</p>
              )}
            </div>

            <div className="flex items-start gap-3 rounded-xl bg-gray-50/60 p-4 ring-1 ring-inset ring-gray-200">
              <input
                type="checkbox"
                id="smsConsent"
                name="smsConsent"
                checked={formData.smsConsent}
                onChange={handleInputChange}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="smsConsent" className="text-sm text-gray-700">
                {t("contact_section.form.sms_consent")}
                <a
                  href="/privacy-policy"
                  className="text-blue-700 hover:text-blue-800 ml-1 font-medium underline underline-offset-2"
                >
                  {t("contact_section.form.privacy_link")}
                </a>
              </label>
            </div>

            <div className="pt-2">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 text-white font-semibold shadow-lg shadow-blue-600/20 hover:from-blue-700 hover:to-indigo-700 disabled:from-blue-400 disabled:to-indigo-400 transition"
                whileHover={{ scale: isSubmitting ? 1 : 1.01 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.99 }}
              >
                {isSubmitting && (
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="white"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="white"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                )}
                {isSubmitting ? t("contact_section.form.sending") : t("contact_section.form.submit_button")}
              </motion.button>
            </div>

            {submitStatus === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <svg
                    className="h-5 w-5 text-emerald-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="font-medium">
                    {t("contact_section.form.success.text")}
                  </p>
                </div>
              </motion.div>
            )}
          </form>

          <div className="border-t border-gray-100 bg-white/70 px-6 py-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-700">
              {t("contact_section.form.phone_support_text")}{" "}
              <a
                href="tel:3108802133"
                className="font-semibold text-blue-700 hover:text-blue-800 underline underline-offset-2"
              >
                +1 3108802133
              </a>
            </div>
            <a
              href="mailto:info@citznhub.com"
              className="text-sm font-medium text-blue-700 hover:text-blue-800 underline underline-offset-2"
            >
              info@citznhub.com
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}